import React, { FormEventHandler, useEffect } from 'react';
import {
  reduxForm,
  Field,
  formValueSelector,
  getFormValues,
  getFormSyncErrors,
  initialize,
} from 'redux-form/immutable';
import { useDispatch, useSelector } from 'react-redux';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Button, Grid, Typography } from '@material-ui/core';
import type { InjectedFormProps } from 'redux-form';
import _ from 'lodash';

import { selectUser, selectOrganization } from 'entities/user/selectors';
import Box from 'retired/Box';
import FormTextField from 'retired/FormTextField';
import PhoneNumber from 'retired/PhoneNumberField';
import { required, isEmail, isPhone } from 'utils/validators';
import { Features } from 'utils/features';
import type { State } from 'reducers';
import type { TypedMap } from 'utils/typed-map';
import { showErrorAction } from 'containers/Notification/actions';
import Organization from 'entities/organization/model';

import * as Actions from './actions';

const requiredValidator = required();
const isEmailValidator = isEmail();
const isPhoneValidator = isPhone();

const FORM_IDENTIFIER = 'app/OrganizationPage/PointOfContact';

type FormData = Pick<Organization, 'email' | 'phone' | 'payrollFlatFile'>;

export const valueSelector: <K extends keyof FormData>(state: State, key: K) => FormData[K] =
  formValueSelector(FORM_IDENTIFIER);

const PointOfContact = ({ submitting, initialized }: InjectedFormProps) => {
  const dispatch = useDispatch();
  const organization = useSelector((state: State) => selectOrganization(state, true));
  const user = useSelector(selectUser);
  const syncErrors = useSelector(getFormSyncErrors(FORM_IDENTIFIER));
  const registeredFields = useSelector((state: State) =>
    state.getIn(['form', FORM_IDENTIFIER, 'registeredFields'])
  );

  useEffect(() => {
    const initialValues = organization?.toJS() || {};
    dispatch(initialize(FORM_IDENTIFIER, initialValues));
  }, []);

  const currentFormValues = useSelector(getFormValues(FORM_IDENTIFIER)) as TypedMap<FormData>;

  if (!organization || !initialized) {
    return <LinearProgress />;
  }

  const submit = (data = currentFormValues.toJS()) => {
    const firstSyncError = Object.values(syncErrors)[0];

    if (firstSyncError) {
      dispatch(showErrorAction(firstSyncError));
    } else {
      const formKeys = registeredFields ? [...registeredFields?.keys()] : [];
      dispatch(Actions.saveAction(_.pick(data, ['id', ...formKeys]), FORM_IDENTIFIER));
    }
  };

  const isCompany = organization.isCompany();

  const handleFormEvent: FormEventHandler = (event) => {
    event.preventDefault();
    submit();
  };

  return (
    // eslint-disable-next-line react/no-unknown-property
    <form noValidate onSubmitCapture={handleFormEvent}>
      <fieldset>
        <Box style={{ paddingTop: 0, marginBottom: 30 }}>
          <h2>Point of Contact</h2>
          <Grid container spacing={3}>
            <Grid item sm={12} md={6}>
              <Field
                id="email"
                name="email"
                type="email"
                label="Email"
                component={FormTextField}
                required
                fullWidth
                validate={[requiredValidator, isEmailValidator]}
                alwaysDisplayErrors
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <Field
                id="phone"
                name="phone"
                type="tel"
                label="Phone Number"
                component={PhoneNumber}
                fullWidth
                country={organization?.locationObject.countryCode}
                validate={[!isCompany && requiredValidator, isPhoneValidator].filter(Boolean)}
                required={!isCompany}
              />
            </Grid>
            {user?.hasFeature(Features.PAYROLL_FLAT_FILE) && (
              <Grid item sm={12} md={6}>
                <Field
                  id="payrollFlatFileAdminEmail"
                  name="payrollFlatFile.adminEmail"
                  component={FormTextField}
                  fullWidth
                  autoComplete="off"
                  label="Payroll notification email address"
                  helperText="This email will receive notifications about payroll run statuses, and any issues that occur. It is recommended you input a payroll distribution list. If blank, payroll emails will be received by all admins."
                  validate={isEmailValidator}
                />
              </Grid>
            )}
          </Grid>
          <Typography align="right" style={{ marginTop: 30 }}>
            <Button type="submit" variant="contained" color="primary" disabled={submitting}>
              Submit
            </Button>
          </Typography>
        </Box>
      </fieldset>
    </form>
  );
};

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default withForm(PointOfContact);
